/**
 * Theme mode: 'system' | 'light' | 'dark'.
 *
 * Stored as `ignite-theme`. The older `ignite-dark` key ('1' / '0') was written
 * on every visit — including the very first, when nobody had chosen anything —
 * so a saved value alone never meant "the user picked this". The old code
 * covered that by auto-following the OS whenever the saved value happened to
 * match it. `migrateLegacy` keeps that meaning: a legacy value equal to the
 * current OS preference becomes 'system'; one that differs was a real choice.
 */

export const THEME_KEY = 'ignite-theme';
export const LEGACY_KEY = 'ignite-dark';
export const MODES = ['system', 'light', 'dark'];

export function systemPrefersDark() {
  try { return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false; }
  catch { return false; }
}

export function migrateLegacy(legacy, systemDark) {
  if (legacy === '1') return systemDark ? 'system' : 'dark';
  if (legacy === '0') return systemDark ? 'light' : 'system';
  return 'system';
}

export function readStoredMode(systemDark = systemPrefersDark()) {
  try {
    const saved = localStorage.getItem(THEME_KEY);
    if (MODES.includes(saved)) return saved;
    return migrateLegacy(localStorage.getItem(LEGACY_KEY), systemDark);
  } catch {
    return 'system';
  }
}

export function storeMode(mode) {
  try {
    localStorage.setItem(THEME_KEY, mode);
    localStorage.removeItem(LEGACY_KEY);
  } catch {}
}

export function resolveDark(mode, systemDark) {
  return mode === 'dark' || (mode === 'system' && systemDark);
}

/**
 * The button cycles through all three, ordered so the FIRST tap from
 * 'system' always visibly changes the screen: system → the opposite of what
 * the device shows → the same as the device → back to system.
 */
export function nextMode(mode, systemDark) {
  const opposite = systemDark ? 'light' : 'dark';
  const same     = systemDark ? 'dark'  : 'light';
  if (mode === 'system')  return opposite;
  if (mode === opposite)  return same;
  return 'system';
}
